import React, { useState, useEffect } from "react";
import Notification from "../../component/notification";
import { Modal, Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { getAuth, deleteUser, signInWithEmailAndPassword } from "firebase/auth";

const StudentList = () => {
  // React Router hooks
  // push comment 
  const location = useLocation();
  const navigate = useNavigate();

  // State for students, search, pagination, notification, modal, and courses
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [notification, setNotification] = useState({
    visible: false,
    message: "",
    color: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [courses, setCourses] = useState([]);
  const studentsPerPage = 10;

  // Fetch students from Firestore
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "students"));
        const studentsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setStudents(studentsData);
      } catch (error) {
        console.error("Error fetching students: ", error);
        setNotification({
          visible: true,
          message: "Error fetching students",
          color: "failure",
        });
      }
    };
    fetchStudents();
  }, []);

  // Fetch courses from Firestore
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "courses"));
        const coursesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCourses(coursesData);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };
    fetchCourses();
  }, []);

  // Add new student from navigation state (after add form)
  useEffect(() => {
    if (location.state && location.state.newStudent) {
      setStudents((prev) => [location.state.newStudent, ...prev]);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Filter and paginate students
  const filtered = students.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / studentsPerPage);
  const paginated = filtered.slice(
    (page - 1) * studentsPerPage,
    page * studentsPerPage
  );

  // Get course title by course ID
  const getCourseTitle = (courseId) => {
    const course = courses.find((c) => c.id === courseId);
    return course ? course.title : courseId;
  };

  // Handle search input
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Navigate to edit student page
  const handleEditStudent = (student) => {
    navigate(`/students/edit/${student.id}`, { state: { student } });
  };

  // Navigate to add student page
  const handleAddCandidate = () => {
    navigate("/students/new");
  };

  // Show delete confirmation modal
  const handleShowDeleteModal = (index) => {
    setStudentToDelete(index);
    setShowDeleteModal(true);
  };

  // Delete student from Firestore and Firebase Authentication
  const handleDeleteStudent = async () => {
    try {
      const studentToDeleteDoc = students[studentToDelete];
      const auth = getAuth();

      // Delete Firestore document
      await deleteDoc(doc(db, "students", studentToDeleteDoc.id));

      // Sign in as the student to be deleted, then delete user auth
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          studentToDeleteDoc.email,
          studentToDeleteDoc.phone
        );
        await deleteUser(userCredential.user);
      } catch (error) {
        console.error("Error signing in to delete user: ", error);
        setNotification({
          visible: true,
          message: `Error deleting student: ${error.message}`,
          color: "failure",
        });
        setShowDeleteModal(false);
        return;
      }

      // Update state after successful deletion
      setStudents((prev) => prev.filter((_, i) => i !== studentToDelete));
      setNotification({
        visible: true,
        message: "❌ Student deleted successfully!",
        color: "success",
      });
      setTimeout(
        () => setNotification({ visible: false, message: "", color: "" }),
        3000
      );
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting student: ", error);
      setNotification({
        visible: true,
        message: `Error deleting student: ${error.message}`,
        color: "failure",
      });
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Notification Banner */}
      <Notification
        message={notification.message}
        color={notification.color}
        visible={notification.visible}
      />
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="fw-bold mb-1">Students</h2>
          <div className="text-muted">
            Manage student information and course assignments
          </div>
        </div>
        <button
          className="btn btn-dark d-flex align-items-center gap-2"
          onClick={handleAddCandidate}
        >
          <i className="bi bi-person-plus"></i> Add Student
        </button>
      </div>
      {/* Main Table Section */}
      <div className="bg-white rounded-3 p-4 shadow-sm border">
        <h4 className="fw-bold mb-1">Student Management</h4>
        <div className="text-muted mb-3">
          View and manage all students in the system
        </div>
        {/* Search Input */}
        <input
          type="text"
          className="form-control mb-3"
          placeholder="Search students..."
          value={search}
          onChange={handleSearch}
          style={{ maxWidth: 350 }}
        />
        {/* Students Table */}
        <div className="table-responsive">
          <table className="table align-middle mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Assigned Course</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No students found.
                  </td>
                </tr>
              ) : (
                paginated.map((student, idx) => (
                  <tr key={idx}>
                    <td>{student.name}</td>
                    <td>{student.email}</td>
                    <td>{getCourseTitle(student.course)}</td>
                    <td>
                      <button
                        className="btn btn-link p-1"
                        title="Edit"
                        onClick={() => handleEditStudent(student)}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button
                        className="btn btn-link p-1 text-danger"
                        title="Delete"
                        onClick={() => handleShowDeleteModal(idx)}
                      >
                        <i className="bi bi-trash3-fill"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Controls */}
    
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Showing{" "}
            {filtered.length === 0 ? 0 : (page - 1) * studentsPerPage + 1} to{" "}
            {Math.min(page * studentsPerPage, filtered.length)} of{" "}
            {filtered.length} entries
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              &lt;
            </button>
            <span className="small">
              Page {page} of {totalPages || 1}
            </span>
            <button
              className="btn btn-light btn-sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages || totalPages === 0}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this student? This will remove the
          student record from the database.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteStudent}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentList;

